import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { useNavigate } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import Tooltip from "ds/components/Tooltip";
import useTypedContext from "hooks/useTypedContext";
import { Run } from "types/generated";

import { useRunShortcut } from "../../hooks/useRunShortcut";
import { PROMOTE_PROPOSED_RUN } from "./gql";

type PromoteRunButtonProps = {
  runId: string;
  stackId: string;
  errorMessage?: string;
};

const PromoteRunButton = ({ runId, stackId, errorMessage }: PromoteRunButtonProps) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const navigate = useNavigate();

  const [runPromote, { data, loading }] = useMutation<{ runPromote: { id: Run["id"] } }>(
    PROMOTE_PROPOSED_RUN,
    {
      variables: {
        stackId,
        runId,
      },
    }
  );

  const promoteRun = useCallback(() => {
    runPromote()
      .then(({ data }) => {
        if (data?.runPromote?.id) {
          reportSuccess({ message: `Run ${data.runPromote.id} successfully promoted` });

          navigate(`/stack/${stackId}/run/${data.runPromote.id}`);
        }
      })
      .catch(onError);
  }, [runPromote, onError, reportSuccess, stackId, navigate]);

  const isLoading = loading || !!data?.runPromote?.id;
  const isDisabled = isLoading || !!errorMessage;

  useRunShortcut({
    label: "Promote",
    code: "mod+alt+g",
    callback: promoteRun,
    loading: isLoading,
    disabled: isDisabled,
  });

  return (
    <Tooltip
      on={(props) => (
        <Button
          {...props}
          disabled={isDisabled}
          loading={isLoading}
          onPress={promoteRun}
          variant="secondary"
          size="small"
        >
          Promote
        </Button>
      )}
    >
      {errorMessage ||
        "Promoting a proposed run is triggering a tracked run for the same Git commit."}
    </Tooltip>
  );
};

export default PromoteRunButton;
